import BodyText from '@lyra/core/components/BodyText'
import Link from '@lyra/core/components/Link'
import Spinner from '@lyra/core/components/Spinner'
import Table, { createColumnHelper, TableProps } from '@lyra/core/components/Table'
import Tooltip from '@lyra/core/components/Tooltip'
import formatNumber from '@lyra/core/utils/formatNumber'
import formatTruncatedAddress from '@lyra/core/utils/formatTruncatedAddress'
import toBigNumber from '@lyra/core/utils/toBigNumber'
import {
  BridgeTransaction,
  BridgeTransactionType,
  yieldTransactionTypes,
} from '@lyra/web/constants/bridge'
import { YieldBridgeTransaction } from '@lyra/web/constants/yield'
import { getIsBridgeTxPending, getTxBridgeStatus } from '@lyra/web/utils/bridge'
import { getDepositNetworkForChainId } from '@lyra/web/utils/chains'
import getChainForChainId from '@lyra/web/utils/getChainForChainId'
import getExplorerUrl from '@lyra/web/utils/getExplorerUrl'
import {
  formatDepositTokenBalance,
  getDepositTokenForAddress,
  getTokenDecimals,
} from '@lyra/web/utils/tokens'
import { getYieldBridgeStatus, getYieldTokenConfigForInputToken } from '@lyra/web/utils/yield'
import { useMemo } from 'react'
import { XStack, YStack } from 'tamagui'

import TimeCell from './TimeCell'
import TokenIcon from './TokenIcon'

type Props = {
  transactions: BridgeTransaction[]
} & Omit<TableProps<BridgeTransaction>, 'columns' | 'data'>

const getActionName = (txType: BridgeTransactionType) => {
  switch (txType) {
    case 'deposit':
      return 'Deposit'
    case 'withdraw':
      return 'Withdrawal'
    case 'predeposit-yield':
    case 'deposit-yield':
      return 'Vault Deposit'
    case 'prewithdraw-yield':
    case 'withdraw-yield':
      return 'Vault Withdrawal'
  }
}

export const columnHelper = createColumnHelper<BridgeTransaction>()
export const transactionsColumns = [
  columnHelper.accessor('timestamp', {
    header: 'Time',
    meta: {
      minWidth: 40,
      maxWidth: 40,
    },
    cell: (props) => {
      return <TimeCell timestamp={props.row.original.timestamp} />
    },
  }),
  columnHelper.accessor('type', {
    header: 'Action',
    meta: {
      minWidth: 160,
    },
    cell: ({ row: { original: tx } }) => {
      const network = getDepositNetworkForChainId(
        tx.type === 'withdraw'
          ? tx.dstChainId
          : tx.type === 'deposit'
          ? tx.srcChainId
          : tx.toStatus.srcChainId
      )
      if (!network) {
        return <BodyText>-</BodyText>
      }
      const { isPending } = getIsBridgeTxPending(tx)
      if (
        tx.type === 'deposit-yield' ||
        tx.type === 'predeposit-yield' ||
        tx.type === 'withdraw-yield' ||
        tx.type === 'prewithdraw-yield'
      ) {
        const config = getYieldTokenConfigForInputToken(tx.token.address, tx.strategy)
        const { status } = getYieldBridgeStatus(tx as YieldBridgeTransaction)
        return (
          <XStack gap="$1.5" alignItems="center">
            {status === 'pending' || !config ? (
              <Spinner color="primary" size={24} />
            ) : (
              <TokenIcon size={24} symbol={config.collateralId} />
            )}
            <YStack>
              <BodyText>{getActionName(tx.type)}</BodyText>
              {config ? (
                <BodyText color="secondary">
                  {formatNumber(tx.amount)} {config.id}
                </BodyText>
              ) : (
                <BodyText>Unsupported token</BodyText>
              )}
            </YStack>
          </XStack>
        )
      }

      const tokenId = getDepositTokenForAddress(network, tx.token.address)
      return (
        <XStack gap="$1.5" alignItems="center">
          {isPending ? (
            <Spinner color="primary" size={24} />
          ) : (
            <TokenIcon size={24} symbol={tokenId} />
          )}
          <YStack>
            <BodyText>{getActionName(tx.type)}</BodyText>
            <BodyText color="secondary">
              {formatDepositTokenBalance(
                toBigNumber(tx.amount, getTokenDecimals(tokenId)),
                tokenId
              )}
            </BodyText>
          </YStack>
        </XStack>
      )
    },
  }),
  columnHelper.accessor('isPending', {
    id: 'status',
    header: 'Status',
    meta: {
      minWidth: 110,
    },
    cell: ({ row: { original: tx } }) => {
      const { status, message } = yieldTransactionTypes.includes(tx.type)
        ? getYieldBridgeStatus(tx as YieldBridgeTransaction)
        : getTxBridgeStatus(tx)
      return message ? (
        <Tooltip
          trigger={
            <BodyText
              color={
                status === 'pending' || status === 'cancelled'
                  ? 'amber'
                  : status === 'reverted'
                  ? 'red'
                  : 'green'
              }
            >
              {status[0].toUpperCase() + status.substr(1).toLowerCase()}
            </BodyText>
          }
          content={<BodyText>{message}</BodyText>}
        />
      ) : (
        <BodyText
          color={
            status === 'pending' || status === 'cancelled'
              ? 'amber'
              : status === 'reverted'
              ? 'red'
              : 'green'
          }
        >
          {status[0].toUpperCase() + status.substr(1).toLowerCase()}
        </BodyText>
      )
    },
  }),
  columnHelper.accessor('network', {
    header: 'Network',
    meta: {
      minWidth: 110,
    },
    cell: (props) => {
      const tx = props.row.original
      const chain =
        tx.type === 'withdraw'
          ? getChainForChainId(tx.dstChainId)
          : tx.type === 'deposit'
          ? getChainForChainId(tx.srcChainId)
          : getChainForChainId(tx.toStatus.srcChainId)
      return <BodyText>{chain?.name ?? '-'}</BodyText>
    },
  }),
  columnHelper.accessor('outboundTxHash', {
    header: 'Outbound Transaction',
    meta: {
      minWidth: 130,
    },
    cell: (props) => {
      const tx = props.row.original
      const txHash =
        tx.type === 'withdraw' || tx.type === 'deposit' ? tx.srcTxHash : tx.toStatus.srcTxHash
      const chain =
        tx.type === 'deposit' || tx.type === 'withdraw'
          ? getChainForChainId(tx.srcChainId)
          : getChainForChainId(tx.toStatus.srcChainId)
      return txHash && chain ? (
        <Link
          label={formatTruncatedAddress(txHash)}
          href={getExplorerUrl(chain, txHash)}
          target="_blank"
          size="md"
        />
      ) : (
        <BodyText>-</BodyText>
      )
    },
  }),
  columnHelper.accessor('inboundTxHash', {
    header: 'Inbound Transaction',
    meta: {
      minWidth: 130,
    },
    cell: (props) => {
      const tx = props.row.original
      const txHash =
        tx.type === 'withdraw' || tx.type === 'deposit' ? tx.dstTxHash : tx.toStatus.dstTxHash
      const chain =
        tx.type === 'deposit' || tx.type === 'withdraw'
          ? getChainForChainId(tx.dstChainId)
          : getChainForChainId(tx.fromStatus.srcChainId)
      return txHash && chain ? (
        <Link
          label={formatTruncatedAddress(txHash)}
          href={getExplorerUrl(chain, txHash)}
          target="_blank"
          size="md"
        />
      ) : (
        <BodyText>-</BodyText>
      )
    },
  }),
]

export default function TransactionsTable({ transactions, ...stackProps }: Props) {
  const userColumns = useMemo(() => transactionsColumns, [])
  return (
    <Table
      {...stackProps}
      size="lg"
      columns={userColumns}
      data={transactions}
      emptyMessage="No transactions"
    />
  )
}
